import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { useEffect, useState, useCallback } from "react";
import { DocumentDTO } from "../types/DocumentDTO";
import { getDocumentDetails, approveDocument, rejectDocument } from "../api/documents";
import DocumentDetails from "../components/DocumentDetails";
import RejectDocumentDialog from "../components/document-details/RejectDocumentDialog";
import { useAppTranslation } from "../hooks/useAppTranslation";

export default function DocumentDetailsPage() {
  const { t } = useAppTranslation("common");

  const navigate = useNavigate();

  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const companyId = searchParams.get("companyId");

  const [doc, setDoc] = useState<DocumentDTO | null>(null);
  const [loading, setLoading] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectLoading, setRejectLoading] = useState(false);

  const loadDocument = useCallback(async () => {
    if (!companyId || !id) return;

    setLoading(true);
    try {
      const updated = await getDocumentDetails(companyId, id);
      setDoc(updated);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [companyId, id]);

  useEffect(() => {
    loadDocument();
  }, [loadDocument]);

  const handleApprove = async () => {
    if (!companyId || !id) return;

    try {
      await approveDocument(companyId, id);
      await loadDocument();
    } catch (e) {
      console.error(e);
      alert(
        e instanceof Error
          ? e.message
          : t("Błąd akceptacji dokumentu")
      );
    }
  };

  const handleReject = async (comment: string) => {
    if (!companyId || !id) return;

    try {
      setRejectLoading(true);
      await rejectDocument(companyId, id, comment);
      await loadDocument();
      setRejectOpen(false);
    } catch (e) {
      console.error(e);
      alert("Błąd odrzucenia dokumentu");
    } finally {
      setRejectLoading(false);
    }
  };

  if (loading) return <p>{t("loading_document")}</p>;
  if (!doc) return <p>{t("document_not_found")}</p>;

  return (
    <>
      <DocumentDetails
        doc={doc}
        companyId={companyId}
        onBack={() => {
          if (window.history.length > 1) {
            navigate(-1);
          } else {
            navigate(`/documents?companyId=${companyId}`);
          }
        }}
        onApprove={handleApprove}
        onReject={() => setRejectOpen(true)}
        onReload={loadDocument}
      />

      <RejectDocumentDialog
        open={rejectOpen}
        loading={rejectLoading}
        onClose={() => setRejectOpen(false)}
        onConfirm={handleReject}
      />
    </>
  );
}
